import { DatePicker, Select, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React from 'react';

import {
  DEFAULT_DATE_RANGE_PRESETS,
  EDateRangePreset,
  findDateRangePreset,
  fromPickerValue,
  getDateRangePresetLabel,
  type IDateRange,
  isDateRangeEmpty,
  resolveDateRange,
  type TDateRangePreset,
  toPickerValue,
} from '../fragments/date-range';
import { SPACING } from '../theme/tokens';

const { RangePicker } = DatePicker;

export interface IDateRangeFilterProps {
  value: IDateRange;
  onChange: (range: IDateRange) => void;
  presets?: readonly TDateRangePreset[];
  endBefore?: Dayjs;
  disabled?: boolean;
}

export const getDateRangePresetOptions = (
  presets: readonly TDateRangePreset[],
  now: Dayjs,
  endBefore?: Dayjs,
): { value: EDateRangePreset; label: string; disabled?: boolean }[] => [
  ...presets.map((preset: TDateRangePreset) => ({
    value: preset,
    label: getDateRangePresetLabel(preset, now),
    disabled: isDateRangeEmpty(resolveDateRange(preset, now, endBefore)),
  })),
  {
    value: EDateRangePreset.CUSTOM,
    label: getDateRangePresetLabel(EDateRangePreset.CUSTOM, now),
    disabled: true,
  },
];

function DateRangeFilter({
  value,
  onChange,
  presets = DEFAULT_DATE_RANGE_PRESETS,
  endBefore,
  disabled,
}: IDateRangeFilterProps): React.ReactElement {
  const now: Dayjs = dayjs();

  return (
    <Space size={SPACING.sm} wrap>
      <Select<EDateRangePreset>
        style={{ width: 180 }}
        value={findDateRangePreset(value, presets, now, endBefore)}
        options={getDateRangePresetOptions(presets, now, endBefore)}
        disabled={disabled}
        onChange={(preset: EDateRangePreset): void => {
          if (preset !== EDateRangePreset.CUSTOM) {
            onChange(resolveDateRange(preset, now, endBefore));
          }
        }}
      />
      <RangePicker
        value={toPickerValue(value)}
        allowClear={false}
        disabled={disabled}
        disabledDate={
          endBefore
            ? (date: Dayjs): boolean => !date.isBefore(endBefore, 'day')
            : undefined
        }
        onChange={(range): void => {
          if (range?.[0] && range[1]) {
            onChange(fromPickerValue(range[0], range[1]));
          }
        }}
      />
    </Space>
  );
}

export default DateRangeFilter;
